<template>
  <v-toolbar 
    class="pa-0 pl-1 d-flex align-center unified-toolbar mt-2 mb-2" 
    color="transparent"
    style="height: auto; min-height: 48px; overflow: visible; flex-shrink: 0;"
  >
    <v-sheet style="width:100%; overflow: visible; position: relative;" class="d-flex align-center pr-4" color="transparent">
      <!-- Tabs Section -->
      <div v-if="$slots.tabs || tabs.length > 0" class="d-flex align-center tabs-wrapper">
        <v-tabs
          v-if="tabs.length > 0"
          :model-value="activeTab"
          class="ml-5 pane-toolbar-tabs"
          :color="primary"
          style="font-size:1em;"
          @update:model-value="$emit('update:activeTab', $event)"
        >
          <v-tab 
            v-for="tab in tabs"
            :key="tab.value"
            :ripple="false" 
            @click="handleTabClick(tab)"
            rounded="0" 
            :value="tab.value" 
            style="font-size:1em; text-transform: capitalize; min-height: 36px;"
          >
            <v-icon v-if="tab.icon" start size="small">{{ tab.icon }}</v-icon>
            <span style="font-variant: none;">{{ tab.label }}</span>
          </v-tab>
        </v-tabs>
        <!-- Custom tabs slot for more complex tab structures -->
        <slot name="tabs"></slot>
      </div>

      <!-- Divider (shown if there are controls or actions) -->
      <v-divider 
        v-if="showDivider"
        :style="'background-color:' + primary" 
        vertical 
        class="mr-4 ml-4"
      ></v-divider>
      
      <!-- Controls Section (middle area for navigation, filters, etc.) -->
      <slot name="controls"></slot>
      
      <v-spacer />
      
      <!-- Actions Section (right side for buttons) -->
      <slot name="actions"></slot>
    </v-sheet>
  </v-toolbar>
</template>

<script setup>
import { computed } from 'vue'
import { useThemeColors } from '../composables/useThemeColors.js'

defineOptions({ name: 'unified-pane-toolbar' })

const { primary } = useThemeColors()

const props = defineProps({
  // Array of tab objects: { value: string, label: string, icon?: string, route?: string }
  tabs: {
    type: Array,
    default: () => []
  },
  // Currently active tab value
  activeTab: {
    type: String,
    default: ''
  },
  // Whether to show the divider between tabs and controls
  showDivider: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:activeTab', 'tab-click'])

function handleTabClick(tab) {
  emit('update:activeTab', tab.value)
  emit('tab-click', tab)
}
</script>

<style scoped>
/* Tabs wrapper */
.tabs-wrapper {
  overflow: visible;
}

/* Tab styling - consistent across all panes */
.pane-toolbar-tabs :deep(.v-tab) {
  color: rgba(0, 0, 0, 0.67) !important;
  min-height: 36px;
  transition: all 0.2s ease;
  background-color: transparent !important;
}

.pane-toolbar-tabs :deep(.v-tab:hover) {
  background-color: transparent !important;
}

.pane-toolbar-tabs :deep(.v-tab--selected) {
  background-color: transparent !important;
  color: #2563eb !important; /* light-blue-darken-3 */
}

.pane-toolbar-tabs :deep(.v-tab--selected .v-icon) {
  color: #2563eb !important; /* light-blue-darken-3 */
}

.pane-toolbar-tabs :deep(.v-tab:not(.v-tab--selected)) {
  background-color: transparent !important;
  color: rgba(0, 0, 0, 0.67) !important;
  font-weight: normal !important;
}

.pane-toolbar-tabs :deep(.v-tab:not(.v-tab--selected) .v-icon) {
  color: rgba(0, 0, 0, 0.67) !important;
}

/* Move slider up inside the tab area */
.pane-toolbar-tabs :deep(.v-tab__slider) {
  bottom: 4px !important;
}

/* Set compact toolbar height to match appointments */
.unified-toolbar :deep(.v-toolbar__content) {
  height: 48px !important;
}
</style>
